import React, { useState } from "react";
import { Link } from "react-router-dom";
import "./AddRecordBatch.css";
import AddRecordBatchStep1 from "./AddRecordBatchStep1";
import AddRecordBatchStep2 from "./AddRecordBatchStep2";
import AddRecordBatchStep3 from "./AddRecordBatchStep3";
import Icon from "@msi/cobalt-react/icon";
import 'bootstrap/dist/css/bootstrap.min.css';
import { useOktaAuth } from "@okta/okta-react";
import ApiService from "../../services/apiService";

interface AddRecordBatchProps { }

const AddRecordBatch: React.FC<AddRecordBatchProps> = () => {
  const [files, setFiles] = useState<FileList | null>(null);
  const [step, setStep] = useState<number>(1); // 1: Upload, 2: Validation, 3: Submit
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [submissionMessage, setSubmissionMessage] = useState<string | null>(
    null
  ); // Store the message
  const { authState } = useOktaAuth();
  const apiService = new ApiService();
  const handleBackButton = () => {
    setStep(1);
    setFiles(null); // Clears the selected file
    setValidationErrors([]); // Clears any validation errors
  };

  const handleSubmit = async () => {
    if (!files) return;
    setIsLoading(true);
    const formData = new FormData();

    for (let i = 0; i < files.length; i++) {
      formData.append("file", files[i]);
    }

    const serviceReturn = await apiService.addAPIViaBatch(formData, authState);
    console.log(serviceReturn?.errorResponse);
    if (serviceReturn?.httpResponse !== null && serviceReturn?.httpResponse.status === 200) {
      setStep(3);
      setFiles(null);
      const data = serviceReturn?.httpResponse.data as { message: string }; // Explicitly type response.data
      setSubmissionMessage(data.message);
    }
    else if (serviceReturn?.httpResponse !== null && serviceReturn?.httpResponse.status === 207) {
      const data = serviceReturn?.httpResponse.data as { message: string; errors: string[] };
      const message = getErrorMessage(data.message, data.errors);
      setValidationErrors(message); // Use an array to be consistent
      setStep(2); // Go to the validation step to display errors
      setFiles(null);
    }
    else {
      setValidationErrors(
        serviceReturn?.errorResponse || ["No error returned!"]
      ); // Use an array to be consistent
      setStep(2); // Go to the validation step to display errors
      setFiles(null);
    }
    setIsLoading(false);
  };

  const getErrorMessage = (message: string, errors: string[]) : string[] => {
    const messages = [];
    messages.push(message);
    if (errors.length > 0) {
      errors.map((error, index) => messages.push(`${index + 1}. ${error}`))
    }
    return messages;
  }

  const renderStepContent = () => {
    switch (step) {
      case 1: // Upload Step
        return <AddRecordBatchStep1 onFileChange={setFiles} />;
      case 2: // Validation Step
        return <AddRecordBatchStep2 validationErrors={validationErrors} />;
      case 3: // Submit Step
        return <AddRecordBatchStep3 message={submissionMessage} />;
      default:
        return null;
    }
  };

  const handleCancel = () => {
    setFiles(null); // Clears the selected file
    setValidationErrors([]); // Clears any validation errors
    setStep(1); // Return to the first step if necessary.
  };

  const handleProgressUpdate = (expectedStep: number) => {
    return step >= expectedStep ? "active" : "";
  };

  return (
    <>
      <div className="page-header">
        <div className="page-header-content">
          <div className="page-header-content-title">
            <div className="icon-group">
              <Link to="/">
                <Icon
                  name="ic_back"
                  size={36}
                  color="primary"
                  className="me-2"
                  style={{ cursor: "pointer" }}

                />
              </Link>
              {/*name="ic_filetype_scv"*/}
              <Icon name="ic_data_table" size={36} color="primary" />
            </div>
            <h1 className="title">Batch Upload API</h1>
            <div
              style={{ cursor: "pointer" }}
              data-bs-toggle="modal"
              data-bs-target="#batchInstructionsModal"
            >
              <Icon
                name="ic_help"
                size={36}
                color="primary"
              />
            </div>
          </div>
          <div className="header-buttons">
            <button
              className="msi-btn msi-btn-action"
              type="button"
              onClick={handleCancel}
            >
              Cancel
            </button>
          </div>
        </div>
      </div>

      <div
        className="modal fade mt-5"
        id="batchInstructionsModal"
        tabIndex={-1}
        role="dialog"
        aria-labelledby="batchInstructionsModalLabel"
        aria-hidden="true"
      >
        <div className="modal-dialog modal-lg" role="document">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title" id="batchInstructionsModalLabel">
                Batch Upload Instructions
              </h5>
              <button
                type="button"
                className="btn-close"
                data-bs-dismiss="modal"
                aria-label="Close"
              ></button>
            </div>
            <div className="modal-body">
              <p>
                Only .csv file format is accepted. {" "}                
              </p>
              <p>
                To perform batch registration, your CSV file must contain only new registration data. The system does not support editing existing registrations via batch. Including existing identifiers will duplicate API entries in the database. {" "}
              </p>
              <p>
                For easy uploading, please check out the API batch upload setup guide.{" "}
                <a href="/guides/Guide%20to%20Add%20API%20Record%20through%20batch%20CSV.pdf" download>
                  Read guide
                </a>
              </p>
              <p>
                You can also download template csv file with example records:{" "}
                <a href="/templates/batch_api_example.csv" download>
                  Template File
                </a>
              </p>
            </div>
            <div className="modal-footer d-flex justify-content-end">
              <button
                type="button"
                className="msi-btn msi-btn-action"
                data-bs-dismiss="modal"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="scroll-container">
        <div className="msi-add-record-batch-content">
          <div className="msi-add-record-batch-content-header">
            <div className="progress-bar">
              <ul className="progress-bar-steps">
                <li className={handleProgressUpdate(1)}>
                  <span className="progress-bar-step">1</span>
                  <p>Upload</p>
                </li>
                <li className={handleProgressUpdate(2)}>
                  <span className="progress-bar-step">2</span>
                  <p>Validation</p>
                </li>
                <li className={handleProgressUpdate(3)}>
                  <span className="progress-bar-step">3</span>
                  <p>Submitted</p>
                </li>
              </ul>
            </div>
          </div>
          
          {
          isLoading ? (
          // center the spinner in the middle of the page
          <div className="d-flex justify-content-center align-items-center vh-100">  
            <div className="spinner-border text-primary" role="status">
              <span className="sr-only"></span>
            </div>
          </div>
        ) : (
          renderStepContent())}

          <div className="footer-buttons">
            <button
              className="msi-btn msi-btn-secondary"
              onClick={handleBackButton}
              disabled={step === 1}
            >
              Back
            </button>
            <button
              className="msi-btn"
              onClick={handleSubmit}
              disabled={isLoading || step === 3 || files === null} // Disable if loading OR on step 3
            >
              {step === 3 ? "Submitted" : "Upload API Batch to Hub"}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default AddRecordBatch;
